<template>
  <Teleport to="body">
    <Transition name="backdrop">
      <div v-if="player" class="drawer-backdrop" @click="$emit('close')" />
    </Transition>
    <Transition name="drawer">
      <aside
        v-if="player"
        class="drawer"
        role="complementary"
        :aria-label="`${player.name} distributions`"
      >
        <header class="drawer__header">
          <div>
            <span class="drawer__role"
              >{{ player.position ?? 'Batter' }} &middot; #{{ player.batting_order ?? '—' }}</span
            >
            <h2 class="drawer__name">{{ player.name }}</h2>
          </div>
          <button class="drawer__close" aria-label="Close" @click="$emit('close')">&#215;</button>
        </header>

        <div class="drawer__charts">
          <div v-for="chart in charts" :key="chart.stat" class="drawer__chart">
            <div class="chart__header">
              <span class="chart__stat">{{ chart.stat }}</span>
              <span class="chart__mean">avg {{ fmtMean(chart.stat) }}</span>
            </div>
            <div v-if="chart.data.length === 0" class="chart__na">N/A</div>
            <template v-else>
              <div class="chart__tooltip">
                <span v-if="hovered?.startsWith(chart.stat + '-')" class="chart__tooltip-text">
                  {{ chart.labels[Number(hovered.split('-')[1])] }}:
                  {{ pct(chart.data[Number(hovered.split('-')[1])]) }}
                </span>
              </div>
              <div class="chart__bars">
                <div
                  v-for="(val, idx) in chart.data"
                  :key="idx"
                  class="bar-col"
                  @mouseenter="hovered = `${chart.stat}-${idx}`"
                  @mouseleave="hovered = null"
                >
                  <div
                    class="bar-fill"
                    :class="{ 'bar-fill--hot': hovered === `${chart.stat}-${idx}` }"
                    :style="{ height: `${val * 100}%` }"
                  />
                  <div class="bar-label">{{ chart.labels[idx] }}</div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { PlayerProjectionResponse } from '../../api/games'

const props = defineProps<{
  player: PlayerProjectionResponse | null
}>()

defineEmits<{ close: [] }>()

const hovered = ref<string | null>(null)
watch(
  () => props.player,
  () => {
    hovered.value = null
  }
)

function makeBuckets(
  data: number[],
  suffix: string,
  maxBuckets: number
): { data: number[]; labels: string[] } {
  if (!data || data.length === 0) return { data: [], labels: [] }
  const raw =
    data.length <= maxBuckets
      ? [...data]
      : [...data.slice(0, maxBuckets - 1), data.slice(maxBuckets - 1).reduce((s, v) => s + v, 0)]
  const labels = raw.map((_, i) =>
    i === raw.length - 1 && data.length > maxBuckets ? `${i}${suffix}+` : `${i}${suffix}`
  )
  return { data: raw, labels }
}

function pct(val: number): string {
  return `${(val * 100).toFixed(1)}%`
}

function fmtMean(stat: string): string {
  if (!props.player) return '—'
  const p = props.player
  const map: Record<string, number | undefined> = {
    H: p.mean_hits,
    HR: p.mean_hr,
    RBI: p.mean_rbi,
    R: p.mean_runs,
    K: p.mean_k,
    BB: p.mean_bb
  }
  const v = map[stat]
  return v !== undefined ? v.toFixed(2) : '—'
}

const charts = computed(() => {
  if (!props.player) return []
  return [
    { stat: 'H', ...makeBuckets(props.player.hit_distribution, 'H', 5) },
    { stat: 'HR', ...makeBuckets(props.player.hr_distribution, 'HR', 3) },
    { stat: 'RBI', ...makeBuckets(props.player.rbi_distribution, 'RBI', 4) },
    { stat: 'R', ...makeBuckets(props.player.runs_distribution, 'R', 4) },
    { stat: 'K', ...makeBuckets(props.player.k_distribution, 'K', 4) },
    { stat: 'BB', ...makeBuckets(props.player.bb_distribution, 'BB', 4) }
  ]
})
</script>

<style>
@import '../../assets/drawer.css';
</style>
